import { isAxiosError } from 'axios';
import { NormalizedErrorType } from 'types/error-log';

const MAX_DEPTH = 6;
const MAX_ARRAY_LENGTH = 100;
const DEFAULT_MAX_LENGTH = 2000;
const AXIOS_RESPONSE_PREVIEW_LENGTH = 500;

export function truncateString(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  let omitted = value.length - maxLength;
  let suffix = `… [truncated ${omitted} chars]`;
  if (suffix.length >= maxLength) {
    return value.slice(0, maxLength);
  }

  let keepLength = maxLength - suffix.length;
  omitted = value.length - keepLength;
  suffix = `… [truncated ${omitted} chars]`;

  while (keepLength + suffix.length > maxLength && keepLength > 0) {
    keepLength -= 1;
    omitted = value.length - keepLength;
    suffix = `… [truncated ${omitted} chars]`;
  }

  if (keepLength + suffix.length > maxLength) {
    return value.slice(0, maxLength);
  }

  return `${value.slice(0, keepLength)}${suffix}`;
}

function isDomNode(value: unknown): value is Node {
  return typeof Node !== 'undefined' && value instanceof Node;
}

function describeDomNode(node: Node): string {
  if (!(node instanceof Element)) {
    return `[${node.nodeName}]`;
  }
  const id = node.id ? `#${node.id}` : '';
  return `<${node.tagName.toLowerCase()}${id}>`;
}

export function toSerializable(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return truncateString(value, DEFAULT_MAX_LENGTH);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint') {
    return `${value.toString()}n`;
  }
  if (typeof value === 'symbol') {
    return value.toString();
  }
  if (typeof value === 'function') {
    return `[Function ${value.name || 'anonymous'}]`;
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (isDomNode(value)) {
    return describeDomNode(value);
  }
  if (depth >= MAX_DEPTH) {
    return '[MaxDepth]';
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);
  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => toSerializable(item, depth + 1, seen));
    if (value.length > MAX_ARRAY_LENGTH) {
      items.push(`[${value.length - MAX_ARRAY_LENGTH} more items]`);
    }
    return items;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      toSerializable(entry, depth + 1, seen)
    ])
  );
}

export function safeStringify(
  value: unknown,
  maxLength = DEFAULT_MAX_LENGTH
): string {
  if (typeof value === 'string') {
    return truncateString(value, maxLength);
  }
  try {
    const serialized = JSON.stringify(toSerializable(value));
    return truncateString(serialized ?? String(value), maxLength);
  } catch {
    return truncateString(String(value), maxLength);
  }
}

function describeAxiosError(error: unknown): string | null {
  if (!isAxiosError(error)) {
    return null;
  }
  const method = error.config?.method?.toUpperCase() ?? '';
  const url = error.config?.url ?? '';
  const status = error.response?.status;
  const statusText = status ? `HTTP ${status}` : 'no response';
  const responsePreview = error.response?.data
    ? ` ${safeStringify(error.response.data, AXIOS_RESPONSE_PREVIEW_LENGTH)}`
    : '';
  return `${error.message} (${statusText} ${method} ${url})${responsePreview}`.trim();
}

export function normalizeError(error: unknown): NormalizedErrorType {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: describeAxiosError(error) ?? error.message,
      stack: error.stack ?? null
    };
  }
  if (typeof error === 'string') {
    return { name: 'Error', message: error, stack: null };
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    return {
      name: typeof record.name === 'string' ? record.name : 'Error',
      message:
        typeof record.message === 'string'
          ? record.message
          : safeStringify(error, AXIOS_RESPONSE_PREVIEW_LENGTH),
      stack: typeof record.stack === 'string' ? record.stack : null
    };
  }
  return { name: 'Error', message: String(error), stack: null };
}

export function formatErrorStack(normalized: NormalizedErrorType): string {
  const headline = `${normalized.name}: ${normalized.message}`;
  if (!normalized.stack) {
    return headline;
  }
  if (normalized.stack.includes(normalized.message)) {
    return normalized.stack;
  }
  return `${headline}\n${normalized.stack}`;
}
